/* TECHNOW — logo neon "tour" rendered off the main thread.
   The SVG light (snippets/technow-logo-animated.liquid) animates stroke-dashoffset forever,
   which forces a full main-thread frame ~60x/s on every page. When OffscreenCanvas + canvas
   filters are available, the same three light strokes are drawn by a Web Worker on a canvas
   laid exactly over the SVG; the SVG light group is hidden only once the worker is ready.
   Anything unsupported keeps the original CSS animation untouched. */
(() => {
  'use strict';
  const ns = window.TECHNOW = window.TECHNOW || {};
  if (ns.logoTour) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  if (motion.matches || typeof OffscreenCanvas === 'undefined' || typeof Worker === 'undefined'
    || !HTMLCanvasElement.prototype.transferControlToOffscreen || typeof Path2D === 'undefined') return;

  // Viewport of the light in SVG user units (covers the route mask region x10..510, y35..130).
  const X0 = 0, Y0 = 35, VW = 516.56, VH = 95, VB_Y = 52.4;

  function workerMain(scope) {
    // Self-contained: this function is serialised into the worker, so it cannot use outer constants.
    const X0 = 0, Y0 = 35;
    const surfaces = new Map();
    let running = true, timer = 0;
    const raf = scope.requestAnimationFrame ? cb => scope.requestAnimationFrame(cb) : cb => setTimeout(cb, 16);
    function bezier(x1, y1, x2, y2) {
      const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
      const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
      const sx = t => ((ax * t + bx) * t + cx) * t, sy = t => ((ay * t + by) * t + cy) * t;
      const dx = t => (3 * ax * t + 2 * bx) * t + cx;
      return x => {
        let t = x;
        for (let i = 0; i < 8; i++) { const e = sx(t) - x, d = dx(t); if (Math.abs(e) < 1e-6 || !d) break; t -= e / d; }
        t = Math.max(0, Math.min(1, t));
        return sy(t);
      };
    }
    const ease = bezier(.35, .15, .65, .85);
    function gradient(ctx, a, b) {
      const g = ctx.createLinearGradient(21, 0, 494, 0);
      g.addColorStop(0, a); g.addColorStop(.76, a); g.addColorStop(.78, b); g.addColorStop(1, b);
      return g;
    }
    function draw(s, now) {
      const ctx = s.ctx, ct = now - s.epoch;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, s.canvas.width, s.canvas.height);
      if (ct < 4000 || !s.k) return;
      const fade = Math.min(1, (ct - 4000) / 300);
      const p = ease(((ct - 4000) % 2500) / 2500), L = s.length, u = L / 100;
      ctx.setTransform(s.k, 0, 0, s.k, -X0 * s.k, -Y0 * s.k);
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      for (const layer of s.layers) {
        ctx.globalAlpha = layer.alpha * fade;
        ctx.filter = layer.blur ? 'blur(' + (layer.blur * s.dpr).toFixed(2) + 'px)' : 'none'; // CSS px, like filter: blur() on the SVG path
        ctx.setLineDash([layer.dash[0] * u, layer.dash[1] * u]);
        ctx.lineDashOffset = -p * L;
        ctx.lineWidth = layer.width;
        ctx.strokeStyle = layer.style;
        ctx.stroke(s.route);
      }
      // Route mask: keep light only within 6 units of the glyph outlines (hides the bridges).
      ctx.filter = 'none'; ctx.globalAlpha = 1; ctx.setLineDash([]);
      ctx.globalCompositeOperation = 'destination-in';
      ctx.lineCap = 'butt'; ctx.lineJoin = 'miter'; ctx.lineWidth = 12; ctx.strokeStyle = '#fff';
      ctx.stroke(s.mask);
      ctx.globalCompositeOperation = 'source-over';
    }
    function loop() {
      timer = 0;
      if (!running || !surfaces.size) return;
      const now = performance.timeOrigin + performance.now();
      surfaces.forEach(s => draw(s, now));
      timer = raf(loop);
    }
    function kick() { if (!timer && running && surfaces.size) timer = raf(loop); }
    scope.onmessage = event => {
      const d = event.data;
      if (d.type === 'init') {
        const ctx = d.canvas.getContext('2d');
        if (!ctx || !('filter' in ctx)) { scope.postMessage({ id: d.id, ok: false }); return; }
        const s = { canvas: d.canvas, ctx, epoch: d.epoch, length: d.length, k: 0,
          route: new Path2D(d.route), mask: new Path2D(d.mask) };
        s.layers = [
          { width: 7, dash: [3.3, 96.7], alpha: .7, blur: 1.4, style: gradient(ctx, '#FF153D', '#B066FF') },
          { width: 2.8, dash: [2, 98], alpha: .95, blur: 0, style: gradient(ctx, '#FF607E', '#CF9FFF') },
          { width: 2.4, dash: [.5, 99.5], alpha: 1, blur: 0, style: gradient(ctx, '#FFC0CC', '#EAD6FF') }
        ];
        surfaces.set(d.id, s);
        scope.postMessage({ id: d.id, ok: true });
        kick();
      } else if (d.type === 'size') {
        const s = surfaces.get(d.id);
        if (s) { s.canvas.width = d.w; s.canvas.height = d.h; s.k = d.k; s.dpr = d.dpr; kick(); }
      } else if (d.type === 'dispose') {
        surfaces.delete(d.id);
      } else if (d.type === 'run') {
        running = d.on; kick();
      }
    };
  }

  let worker = null, nextId = 0;
  const roots = new Map();
  function getWorker() {
    if (worker) return worker;
    const url = URL.createObjectURL(new Blob(['(' + workerMain + ')(self);'], { type: 'text/javascript' }));
    worker = new Worker(url);
    worker.onmessage = event => {
      const entry = [...roots.values()].find(r => r.id === event.data.id);
      if (!entry) return;
      if (event.data.ok) {
        entry.root.setAttribute('data-tour-canvas', '');
      } else {
        entry.dispose();
      }
    };
    return worker;
  }

  function attach(root) {
    if (roots.has(root)) return;
    const svg = root.querySelector('.tnw-logo-contour__svg');
    const halo = root.querySelector('.tnw-logo-contour__halo');
    const maskPath = root.querySelector('mask path');
    if (!svg || !halo || !maskPath) return;
    const length = halo.getTotalLength();
    if (!(length > 0)) return;
    const anim = halo.getAnimations ? halo.getAnimations().find(a => a.animationName === 'tnw-logo-contour-tour') : null;
    const elapsed = anim && anim.currentTime != null ? Number(anim.currentTime) : 0;
    const epoch = performance.timeOrigin + performance.now() - elapsed;

    const canvas = document.createElement('canvas');
    canvas.className = 'tnw-logo-contour__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    root.appendChild(canvas);
    let offscreen;
    try { offscreen = canvas.transferControlToOffscreen(); } catch (_) { canvas.remove(); return; }
    const w = getWorker();
    const id = ++nextId;
    let size = '';
    const place = () => {
      const r = svg.getBoundingClientRect(), base = root.getBoundingClientRect();
      if (r.width < 1) return;
      const s = r.width / VW, dpr = Math.min(window.devicePixelRatio || 1, 3);
      const left = r.left - base.left + X0 * s, top = r.top - base.top + (Y0 - VB_Y) * s;
      canvas.style.left = left + 'px'; canvas.style.top = top + 'px';
      canvas.style.width = VW * s + 'px'; canvas.style.height = VH * s + 'px';
      const pw = Math.round(VW * s * dpr), ph = Math.round(VH * s * dpr), key = pw + 'x' + ph;
      if (key !== size) { size = key; w.postMessage({ type: 'size', id, w: pw, h: ph, k: s * dpr, dpr }); }
    };
    const ro = new ResizeObserver(place);
    const entry = { id, root, dispose() {
      ro.disconnect(); w.postMessage({ type: 'dispose', id });
      root.removeAttribute('data-tour-canvas'); canvas.remove(); roots.delete(root);
    } };
    roots.set(root, entry);
    w.postMessage({ type: 'init', id, canvas: offscreen, epoch, length,
      route: halo.getAttribute('d'), mask: maskPath.getAttribute('d') }, [offscreen]);
    ro.observe(svg); ro.observe(root);
    place();
  }

  function init(scope) {
    (scope || document).querySelectorAll('.technow-logo[data-technow-contour]').forEach(root => {
      try { attach(root); } catch (_) { /* keep CSS animation */ }
    });
  }
  ns.logoTour = { init };
  document.addEventListener('visibilitychange', () => { if (worker) worker.postMessage({ type: 'run', on: !document.hidden }); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    roots.forEach((entry, root) => { if (event.target.contains(root)) entry.dispose(); });
  });
  motion.addEventListener && motion.addEventListener('change', () => { if (motion.matches) roots.forEach(entry => entry.dispose()); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
